/**
 * LogsViewer Page
 *
 * Admin interface for viewing application logs including user actions,
 * errors, API calls, and other application events.
 */

import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import './LogsViewer.css';

export default function LogsViewer() {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter state
  const [logLevel, setLogLevel] = useState('');
  const [eventType, setEventType] = useState('');
  const [limit, setLimit] = useState(100);
  const [expandedLog, setExpandedLog] = useState(null);

  // Auto-refresh state
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(null);

  useEffect(() => {
    loadLogs();
  }, []);

  // Handle auto-refresh
  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(loadLogs, 10000); // Refresh every 10 seconds
      setRefreshInterval(interval);
    } else {
      if (refreshInterval) {
        clearInterval(refreshInterval);
        setRefreshInterval(null);
      }
    }

    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [autoRefresh]);

  async function loadLogs() {
    try {
      setLoading(true);
      setError(null);

      const { data, error: rpcError } = await supabase
        .rpc('get_application_logs', {
          p_limit: limit,
          p_offset: 0,
          p_log_level: logLevel || null,
          p_event_type: eventType || null,
          p_user_id: null,
          p_contractor_id: null,
          p_start_date: null,
          p_end_date: null
        });

      if (rpcError) throw rpcError;

      setLogs(data || []);
    } catch (err) {
      console.error('Error loading logs:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function getLogLevelColor(level) {
    switch (level) {
      case 'error':
        return 'red';
      case 'warn':
        return 'orange';
      case 'info':
        return 'blue';
      case 'debug':
        return 'gray';
      default:
        return 'gray';
    }
  }

  function getLogLevelIcon(level) {
    switch (level) {
      case 'error':
        return '❌';
      case 'warn':
        return '⚠️';
      case 'info':
        return 'ℹ️';
      case 'debug':
        return '🔍';
      default:
        return '📝';
    }
  }

  function getEventTypeIcon(eventType) {
    switch (eventType) {
      case 'page_view':
        return '👁️';
      case 'user_action':
        return '🎯';
      case 'api_call':
        return '🌐';
      case 'error':
        return '🐛';
      case 'console_error':
        return '💥';
      default:
        return '📋';
    }
  }

  function formatEventType(eventType) {
    if (!eventType) return '';
    return eventType.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  }

  function formatDetails(details) {
    if (!details || typeof details !== 'object') return null;

    const entries = Object.entries(details);
    if (entries.length === 0) return null;

    return (
      <div className="log-details">
        {entries.map(([key, value]) => (
          <div key={key} className="log-detail-item">
            <span className="log-detail-key">{key.replace(/_/g, ' ')}:</span>
            <span className="log-detail-value">
              {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
            </span>
          </div>
        ))}
      </div>
    );
  }

  function toggleLogExpansion(logId) {
    setExpandedLog(expandedLog === logId ? null : logId);
  }

  if (loading && logs.length === 0) {
    return (
      <div className="logs-viewer-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading application logs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="logs-viewer-container">
      <div className="logs-viewer-header">
        <div>
          <h1>Application Logs</h1>
          <p>Real-time monitoring of user actions, errors, and application events</p>
        </div>

        <div className="header-actions">
          <label className="auto-refresh-toggle">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            <span>Auto-refresh (10s)</span>
          </label>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <strong>Error:</strong> {error}
        </div>
      )}

      <div className="logs-viewer-filters">
        <div className="filter-group">
          <label>Log Level:</label>
          <select
            value={logLevel}
            onChange={(e) => setLogLevel(e.target.value)}
          >
            <option value="">All Levels</option>
            <option value="error">Error</option>
            <option value="warn">Warning</option>
            <option value="info">Info</option>
            <option value="debug">Debug</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Event Type:</label>
          <select
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
          >
            <option value="">All Events</option>
            <option value="page_view">Page View</option>
            <option value="user_action">User Action</option>
            <option value="api_call">API Call</option>
            <option value="error">Error</option>
            <option value="console_error">Console Error</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Limit:</label>
          <select
            value={limit}
            onChange={(e) => setLimit(parseInt(e.target.value))}
          >
            <option value="50">50</option>
            <option value="100">100</option>
            <option value="200">200</option>
            <option value="500">500</option>
          </select>
        </div>

        <button className="btn-filter" onClick={loadLogs}>
          Apply Filters
        </button>

        <button className="btn-refresh" onClick={loadLogs}>
          🔄 Refresh
        </button>
      </div>

      <div className="logs-stats">
        <div className="stat">
          <span className="stat-value">{logs.length}</span>
          <span className="stat-label">Total Logs</span>
        </div>
        <div className="stat">
          <span className="stat-value">{logs.filter(l => l.log_level === 'error').length}</span>
          <span className="stat-label">Errors</span>
        </div>
        <div className="stat">
          <span className="stat-value">{logs.filter(l => l.log_level === 'warn').length}</span>
          <span className="stat-label">Warnings</span>
        </div>
      </div>

      {logs.length === 0 ? (
        <div className="empty-state">
          <p>No logs found matching your filters</p>
        </div>
      ) : (
        <div className="logs-list">
          {logs.map(log => {
            const isExpanded = expandedLog === log.id;

            return (
              <div
                key={log.id}
                className={`log-card log-${getLogLevelColor(log.log_level)} ${isExpanded ? 'expanded' : ''}`}
              >
                <div className="log-main" onClick={() => toggleLogExpansion(log.id)}>
                  <div className="log-icon">
                    {getLogLevelIcon(log.log_level)}
                  </div>

                  <div className="log-content">
                    <div className="log-header-row">
                      <div className="log-title">
                        <span className="log-level-badge">{log.log_level.toUpperCase()}</span>
                        <span className="log-event-type">
                          {getEventTypeIcon(log.event_type)} {formatEventType(log.event_type)}
                        </span>
                        {log.action && (
                          <span className="log-action">· {log.action}</span>
                        )}
                      </div>
                      <span className="log-time">
                        {new Date(log.created_at).toLocaleString()}
                      </span>
                    </div>

                    <div className="log-message">{log.message}</div>

                    <div className="log-meta">
                      {log.user_email && (
                        <span className="log-user">
                          User: <strong>{log.user_name || log.user_email}</strong>
                        </span>
                      )}
                      {log.contractor_name && (
                        <span className="log-contractor">
                          Company: <strong>{log.contractor_name}</strong>
                        </span>
                      )}
                      {log.session_id && (
                        <span className="log-session">
                          Session: <code>{log.session_id.slice(0, 8)}...</code>
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="log-expand-icon">
                    {isExpanded ? '▼' : '▶'}
                  </div>
                </div>

                {isExpanded && (
                  <div className="log-expanded-content">
                    {log.error_message && (
                      <div className="log-error-section">
                        <h4>Error Message</h4>
                        <div className="error-message">{log.error_message}</div>
                      </div>
                    )}

                    {log.error_stack && (
                      <div className="log-error-section">
                        <h4>Stack Trace</h4>
                        <pre className="error-stack">{log.error_stack}</pre>
                      </div>
                    )}

                    {log.details && formatDetails(log.details)}

                    {log.url && (
                      <div className="log-url-section">
                        <h4>URL</h4>
                        <code>{log.url}</code>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
